import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listBlueprints, getBlueprint, exportTenantAsBlueprint, applyBlueprint,
  deleteBlueprint, listTargetableTenants, getBlueprintRevision,
} from "@/lib/blueprints.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Download, Upload, Trash2, FileJson, Layers } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/blueprints")({
  component: BlueprintsPage,
});

function BlueprintsPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listBlueprints);
  const exportFn = useServerFn(exportTenantAsBlueprint);
  const deleteFn = useServerFn(deleteBlueprint);

  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", notes: "" });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: list, isLoading } = useQuery({
    queryKey: ["blueprints", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const exportMut = useMutation({
    mutationFn: () => exportFn({ data: {
      tenantId: tenantId!, name: form.name.trim(),
      description: form.description.trim() || null,
      notes: form.notes.trim() || null,
    } }),
    onSuccess: (r) => {
      toast.success(`Blueprint saved — revision ${r.revision}`);
      setExporting(false);
      setForm({ name: "", description: "", notes: "" });
      qc.invalidateQueries({ queryKey: ["blueprints", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Export failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { blueprintId: id } }),
    onSuccess: () => {
      toast.success("Blueprint deleted");
      qc.invalidateQueries({ queryKey: ["blueprints", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Blueprints"
        description="Export this tenant's operational policy as a versioned Blueprint and apply it to other tenants. Infrastructure invariants are never carried by a Blueprint."
        actions={
          <Button onClick={() => setExporting(true)} disabled={!tenantId}>
            <Download className="size-4 mr-2" /> Export current tenant
          </Button>
        }
      />

      <div className="rounded-md border border-border bg-muted/40 p-3 flex items-start gap-2 text-sm">
        <ShieldCheck className="size-4 mt-0.5 text-muted-foreground" />
        <div>
          <div className="font-medium">Invariant guardrail</div>
          <div className="text-muted-foreground">
            Blueprints carry only Operational Policy: billing rules, hold-release rules,
            customer templates, trace flags, and reorder thresholds. Facilities, locations,
            inventory, ledger, roles, and audit history are never included or mutated by Apply.
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : !list || list.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Layers className="size-8 mx-auto mb-2 opacity-50" />
          <p>No blueprints yet. Export this tenant to create the first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((bp: any) => (
            <div key={bp.id} className="flex items-center justify-between rounded-md border border-border p-4 hover:bg-muted/40">
              <button className="flex-1 text-left" onClick={() => setOpenId(bp.id)}>
                <div className="font-medium">{bp.name}</div>
                <div className="text-sm text-muted-foreground">
                  {bp.description || <span className="italic">No description</span>}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="outline">rev {bp.current_revision}</Badge>
                  <span className="text-xs text-muted-foreground">
                    updated {new Date(bp.updated_at).toLocaleString()}
                  </span>
                </div>
              </button>
              <Button
                variant="ghost" size="sm"
                onClick={() => { if (confirm(`Delete blueprint "${bp.name}"? Revisions and apply history will be removed.`)) delMut.mutate(bp.id); }}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Export dialog */}
      <Dialog open={exporting} onOpenChange={setExporting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export current tenant as Blueprint</DialogTitle>
            <DialogDescription>
              Captures a versioned snapshot of this tenant's policy configuration. Re-exporting under
              the same name creates a new revision.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="bp-name">Name</Label>
              <Input id="bp-name" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Standard 3PL DTC" />
            </div>
            <div>
              <Label htmlFor="bp-desc">Description</Label>
              <Textarea id="bp-desc" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What kind of tenant this Blueprint sets up" />
            </div>
            <div>
              <Label htmlFor="bp-notes">Revision notes</Label>
              <Input id="bp-notes" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes for this revision" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExporting(false)}>Cancel</Button>
            <Button
              onClick={() => exportMut.mutate()}
              disabled={form.name.trim().length < 2 || exportMut.isPending}
            >
              {exportMut.isPending ? "Exporting…" : "Export"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlueprintSheet blueprintId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function BlueprintSheet({ blueprintId, onClose }: { blueprintId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const getFn = useServerFn(getBlueprint);
  const getRevFn = useServerFn(getBlueprintRevision);
  const targetsFn = useServerFn(listTargetableTenants);
  const applyFn = useServerFn(applyBlueprint);

  const [viewRevId, setViewRevId] = useState<string | null>(null);
  const [applyRevId, setApplyRevId] = useState<string | null>(null);
  const [targetTenant, setTargetTenant] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["blueprint", blueprintId],
    enabled: !!blueprintId,
    queryFn: () => getFn({ data: { blueprintId: blueprintId! } }),
  });

  const { data: targets } = useQuery({
    queryKey: ["blueprint-targets"],
    enabled: !!blueprintId,
    queryFn: () => targetsFn({}),
  });

  const { data: rev } = useQuery({
    queryKey: ["blueprint-revision", viewRevId],
    enabled: !!viewRevId,
    queryFn: () => getRevFn({ data: { revisionId: viewRevId! } }),
  });

  const applyMut = useMutation({
    mutationFn: () => applyFn({ data: { revisionId: applyRevId!, targetTenantId: targetTenant, dryRun: false } }),
    onSuccess: (r) => {
      toast.success(`Blueprint applied (${r.status})`);
      setApplyRevId(null); setTargetTenant("");
      qc.invalidateQueries({ queryKey: ["blueprint", blueprintId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Apply failed"),
  });

  return (
    <Sheet open={!!blueprintId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{data?.blueprint.name ?? "Blueprint"}</SheetTitle>
          <SheetDescription>{data?.blueprint.description ?? ""}</SheetDescription>
        </SheetHeader>

        {isLoading || !data ? <Skeleton className="h-40 mt-4" /> : (
          <div className="space-y-6 mt-4">
            <section>
              <h3 className="text-sm font-semibold mb-2">Revisions</h3>
              <div className="space-y-2">
                {data.revisions.map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between rounded-md border border-border p-3">
                    <div>
                      <div className="font-medium">Revision {r.revision}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()} · {r.content_hash.slice(0, 10)}…
                      </div>
                      {r.notes ? <div className="text-xs mt-1">{r.notes}</div> : null}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setViewRevId(r.id)}>
                        <FileJson className="size-4 mr-1" /> View
                      </Button>
                      <Button size="sm" onClick={() => setApplyRevId(r.id)}>
                        <Upload className="size-4 mr-1" /> Apply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold mb-2">Apply History</h3>
              {data.applications.length === 0 ? (
                <p className="text-sm text-muted-foreground">Not applied yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.applications.map((a: any) => (
                    <div key={a.id} className="rounded-md border border-border p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <Badge variant={a.status === "succeeded" ? "default" : "secondary"}>{a.status}</Badge>
                        <span className="text-xs text-muted-foreground">{new Date(a.applied_at).toLocaleString()}</span>
                      </div>
                      <div className="text-xs mt-1 text-muted-foreground">target {a.target_tenant_id.slice(0, 8)}…</div>
                      <pre className="text-xs mt-2 bg-muted/40 rounded p-2 overflow-x-auto">
                        {JSON.stringify(a.summary, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Revision viewer */}
        <Dialog open={!!viewRevId} onOpenChange={(o) => !o && setViewRevId(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Revision envelope</DialogTitle>
              <DialogDescription>Read-only view of the Blueprint contents.</DialogDescription>
            </DialogHeader>
            <pre className="text-xs bg-muted rounded p-3 max-h-[60vh] overflow-auto">
              {rev ? JSON.stringify(rev.envelope, null, 2) : "Loading…"}
            </pre>
          </DialogContent>
        </Dialog>

        {/* Apply dialog */}
        <Dialog open={!!applyRevId} onOpenChange={(o) => !o && (setApplyRevId(null), setTargetTenant(""))}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Apply Blueprint</DialogTitle>
              <DialogDescription>
                Pick the target tenant. Only Operational Policy is applied — Infrastructure Invariants
                are never touched. Existing entries with the same key are skipped (idempotent).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Target tenant</Label>
              <Select value={targetTenant} onValueChange={setTargetTenant}>
                <SelectTrigger><SelectValue placeholder="Select a tenant you administer" /></SelectTrigger>
                <SelectContent>
                  {(targets ?? []).map((t: any) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setApplyRevId(null); setTargetTenant(""); }}>Cancel</Button>
              <Button
                onClick={() => applyMut.mutate()}
                disabled={!targetTenant || applyMut.isPending}
              >
                {applyMut.isPending ? "Applying…" : "Apply Blueprint"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}